// CMS bodies/descriptions often arrive as HTML ("<p>Line one</p><p>Line&nbsp;two</p>").
// Ad text inputs need plain text, but the paragraph/line structure should survive.

const HTML_PATTERN = /<\/?[a-z!][^>]*>|&(#\d+|#x[0-9a-f]+|[a-z]+);/i;

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'",
  rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“',
  ndash: '–', mdash: '—', hellip: '…', bull: '•',
  copy: '©', reg: '®', trade: '™', euro: '€',
};

const decodeEntities = (text: string) =>
  text.replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (match, code: string) => {
    const lower = code.toLowerCase();
    if (lower.startsWith('#x')) return String.fromCodePoint(parseInt(lower.slice(2), 16));
    if (lower.startsWith('#')) return String.fromCodePoint(parseInt(lower.slice(1), 10));
    return NAMED_ENTITIES[lower] ?? match;
  });

/**
 * Converts HTML to plain text, keeping paragraphs and line breaks as newlines
 * (or as spaces when `multiline` is false, for single-line inputs). Text with
 * no tags or entities is returned untouched, so typing is never altered.
 */
export const htmlToPlainText = (value: any, multiline = true): any => {
  if (typeof value !== 'string' || !HTML_PATTERN.test(value)) return value;

  let text = value
    // Raw newlines in HTML source are just whitespace; only tags make line breaks.
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/\s*<br\s*\/?>\s*/gi, '\n')
    .replace(/<li[^>]*>/gi, '\n\u2022 ')
    .replace(/<\/(p|div|h[1-6]|ul|ol|blockquote|tr)>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  text = decodeEntities(text)
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return multiline ? text : text.replace(/\s*\n+\s*/g, ' ');
};
